/**
 * ZavadyEditor — the damage map as a two-column editor (plan 3.16): the exterior drawing or the interior picker on the
 * left, the legend, the selected part's form and the list of faults on the right. Pure presentation over a list of
 * faults: it knows nothing about WHERE they live. The per-car Závady modal saves each part through the API; the return
 * dialog stages the changes and sends them with the protocol. Each passes what a save / a repair does. A repaired fault
 * leaves the map but stays on the car (P4-h); deleting one outright is a separate, rarer action (a mistake).
 */

import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { SegmentedControl } from '@/components/SegmentedControl';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import type { Dil, Zavada, ZavadaBody, Zavaznost, ZonaDilu } from './api';
import { ExteriorMap, SEVERITY_FILL, ZAVAZNOST_TEXT } from './ExteriorMap';
import { EXTERIOR_PARTS } from './exterior-map-data';
import { PartPicker } from './PartPicker';

const SEVERITIES: { value: Zavaznost; label: string }[] = [
  { value: 'nizka', label: 'Drobná' },
  { value: 'stredni', label: 'Střední' },
  { value: 'vysoka', label: 'Závažná' },
];

const TONE: Record<Zavaznost, 'neutral' | 'warn' | 'danger'> = { nizka: 'neutral', stredni: 'warn', vysoka: 'danger' };

interface ZavadyEditorProps {
  faults: Zavada[];
  parts: Dil[];
  canWrite: boolean;
  /** Frozen: a request is in flight and the selection must not move under it. */
  busy: boolean;
  onSave: (dil: number, body: ZavadaBody) => void;
  /** The fault is repaired (`poznamka`: what was done, may be empty); absent = no repair is offered (the return dialog: nothing is repaired during a rental). */
  onRepair?: (dil: number, poznamka: string) => void;
  /** Delete a fault entered by mistake; absent = the action is not offered. */
  onDelete?: (fault: Zavada) => void;
  /** Narrows the repair to some faults. */
  canRepair?: (fault: Zavada) => boolean;
  /** Narrows `onDelete` to some faults (the return dialog may drop only what it staged itself). */
  canDelete?: (fault: Zavada) => boolean;
  /** The label of that action; the car's page deletes records, the return dialog only drops a fault staged there. */
  deleteLabel?: string;
}

export function ZavadyEditor({ faults, parts, canWrite, busy, onSave, onRepair, canRepair, onDelete, canDelete, deleteLabel = 'Smazat omylem zadanou' }: ZavadyEditorProps) {
  const [selected, setSelected] = useState<number | null>(null);
  const [zona, setZona] = useState<ZonaDilu>('exterior');
  const severities = useMemo(() => new Map(faults.map((z) => [z.id_dilu, z.zavaznost])), [faults]);
  const partById = useMemo(() => new Map(parts.map((d) => [d.id_dilu, d])), [parts]);
  // Exterior parts the drawing has no shape for ("jiné"): listed under the map instead.
  const drawn = useMemo(() => new Set(EXTERIOR_PARTS.map((p) => p.id)), []);
  const offMap = useMemo(() => parts.filter((d) => d.zona === 'exterior' && !drawn.has(d.id_dilu)), [parts, drawn]);
  const interior = useMemo(() => parts.filter((d) => d.zona === 'interior'), [parts]);
  const nameOf = (id: number) => partById.get(id)?.nazev ?? `Díl ${id}`;
  // Picking a part from the fault list brings its view to the front, so the selection is always visible.
  const choose = (id: number) => {
    setSelected(id);
    const z = partById.get(id)?.zona;
    if (z) setZona(z);
  };
  // The form is bound to `selected`: switching the view must not leave it editing a part that is no longer
  // on screen (a save would change something the user cannot see).
  const switchZona = (z: ZonaDilu) => {
    setZona(z);
    if (selected != null && partById.get(selected)?.zona !== z) setSelected(null);
  };
  const count = (z: ZonaDilu) => faults.filter((f) => f.zona === z).length;
  const current = selected != null ? faults.find((z) => z.id_dilu === selected) : undefined;

  return (
    <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-col gap-4">
        <SegmentedControl<ZonaDilu>
          aria-label="Část vozidla"
          options={[
            { value: 'exterior', label: `Vnějšek · ${count('exterior')}` },
            { value: 'interior', label: `Interiér · ${count('interior')}` },
          ]}
          value={zona}
          onChange={(z) => !busy && switchZona(z)}
        />
        {zona === 'exterior' ? (
          <>
            <ExteriorMap faults={severities} selected={selected} onSelect={choose} disabled={busy} />
            {offMap.length > 0 && (
              <PartPicker
                parts={offMap}
                fallbackGroup="Mimo mapu"
                testId="zavady-mimo-mapu"
                faults={severities}
                selected={selected}
                onSelect={choose}
                disabled={busy}
              />
            )}
          </>
        ) : (
          <PartPicker
            parts={interior}
            testId="zavady-interier"
            faults={severities}
            selected={selected}
            onSelect={choose}
            disabled={busy}
          />
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-5">
        <Legend />
        {selected == null ? (
          <p data-testid="zavady-vyber" className="text-[13px] text-ink-2">
            {canWrite ? 'Vyberte díl a zapište jeho závadu.' : 'Vyberte díl.'}
          </p>
        ) : (
          <PartPanel
            key={`${selected}:${current?.zmeneno_at ?? 'none'}`}
            dil={selected}
            name={nameOf(selected)}
            fault={current}
            canWrite={canWrite}
            busy={busy}
            onSave={(b) => onSave(selected, b)}
            onRepair={current && onRepair && (canRepair?.(current) ?? true) ? (poznamka) => onRepair(selected, poznamka) : undefined}
            onDelete={current && onDelete && (canDelete?.(current) ?? true) ? () => onDelete(current) : undefined}
            deleteLabel={deleteLabel}
          />
        )}
        <FaultList faults={faults} selected={selected} onSelect={choose} disabled={busy} nameOf={nameOf} />
      </div>
    </div>
  );
}

export function Legend() {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-2" aria-label="Legenda závažnosti">
      {SEVERITIES.map((s) => (
        <li key={s.value} className="flex items-center gap-2">
          {/* the swatch is the map's own fill, so the legend can't drift from the drawing */}
          <svg viewBox="0 0 10 10" className="size-3" aria-hidden>
            <rect x="0.5" y="0.5" width="9" height="9" strokeWidth="1" className={SEVERITY_FILL[s.value]} />
          </svg>
          {ZAVAZNOST_TEXT[s.value]}
        </li>
      ))}
    </ul>
  );
}

export function FaultList({
  faults,
  selected,
  onSelect,
  disabled,
  nameOf,
}: {
  faults: Zavada[];
  selected: number | null;
  onSelect: (id: number) => void;
  disabled: boolean;
  nameOf: (id: number) => string;
}) {
  if (faults.length === 0) {
    return (
      <p data-testid="zavady-prazdno" className="text-[13px] text-ink-2">
        Vozidlo nemá zaznamenanou žádnou závadu.
      </p>
    );
  }
  return (
    <div data-testid="zavady-seznam" className="flex flex-col">
      <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Závady · {faults.length}
      </div>
      <ul className="border border-line">
        {faults.map((z) => (
          <li key={z.id_dilu} className="border-b border-line last:border-b-0">
            <button
              type="button"
              disabled={disabled}
              aria-current={selected === z.id_dilu}
              onClick={() => onSelect(z.id_dilu)}
              className={`flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-accent-soft disabled:cursor-not-allowed ${
                selected === z.id_dilu ? 'bg-accent-soft' : ''
              }`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] text-ink">
                  {nameOf(z.id_dilu)}
                  {z.zona === 'interior' && <span className="text-ink-3"> · interiér</span>}
                </span>
                <span className="block truncate text-[12px] text-ink-2">{z.popis}</span>
              </span>
              <Badge tone={TONE[z.zavaznost]} size="sm">
                {ZAVAZNOST_TEXT[z.zavaznost]}
              </Badge>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface PartPanelProps {
  dil: number;
  name: string;
  fault: Zavada | undefined;
  canWrite: boolean;
  busy: boolean;
  onSave: (body: { zavaznost: Zavaznost; popis: string }) => void;
  /** Absent = the fault cannot be repaired here. */
  onRepair?: (poznamka: string) => void;
  onDelete?: () => void;
  deleteLabel: string;
}

/** One part: its name and, at write, the severity + text form. Remounted (key) when the stored fault changes. */
export function PartPanel({ name, fault, canWrite, busy, onSave, onRepair, onDelete, deleteLabel }: PartPanelProps) {
  const [poznamka, setPoznamka] = useState('');
  const [zavaznost, setZavaznost] = useState<Zavaznost>(fault?.zavaznost ?? 'nizka');
  const [popis, setPopis] = useState(fault?.popis ?? '');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!popis.trim()) {
      toast.error('Vyplňte popis závady.');
      return;
    }
    onSave({ zavaznost, popis: popis.trim() });
  };

  if (!canWrite) {
    return (
      <div data-testid="zavady-dil" className="flex flex-col gap-1">
        <div className="text-[14px] text-ink">{name}</div>
        {fault ? (
          <div className="text-[13px] text-ink-2">
            {ZAVAZNOST_TEXT[fault.zavaznost]}: {fault.popis}
          </div>
        ) : (
          <div className="text-[13px] text-ink-2">Bez závady.</div>
        )}
      </div>
    );
  }

  const repair = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    onRepair?.(poznamka.trim());
  };

  return (
    <div data-testid="zavady-dil" className="flex flex-col gap-4 border border-line p-4">
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="text-[14px] text-ink">{name}</div>
        <SegmentedControl
          aria-label="Závažnost"
          options={SEVERITIES}
          value={zavaznost}
          onChange={(v) => !busy && setZavaznost(v)}
        />
        <Field label="Popis závady" required hint="Nejvýš 63 znaků.">
          <TextInput value={popis} maxLength={63} disabled={busy} onChange={(e) => setPopis(e.currentTarget.value)} />
        </Field>
        <div className="flex gap-2">
          <Button variant="ink" size="sm" type="submit" disabled={busy}>
            {fault ? 'Uložit závadu' : 'Přidat závadu'}
          </Button>
        </div>
      </form>
      {fault && (onRepair || onDelete) && (
        <form onSubmit={repair} data-testid="zavady-oprava" className="flex flex-col gap-3 border-t border-line pt-4">
          {onRepair && (
            <Field label="Co se opravilo" hint="Nepovinné, nejvýš 63 znaků. Závada zůstane na vozidle jako opravená.">
              <TextInput value={poznamka} maxLength={63} disabled={busy} onChange={(e) => setPoznamka(e.currentTarget.value)} />
            </Field>
          )}
          <div className="flex flex-wrap gap-2">
            {onRepair && (
              <Button variant="secondary" size="sm" type="submit" disabled={busy}>
                Opraveno
              </Button>
            )}
            {onDelete && (
              <Button variant="secondary" size="sm" onClick={onDelete} disabled={busy}>
                {deleteLabel}
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
